import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useRealtimeSubscription } from '../lib/realtime';
import { createInvoice } from '../lib/invoiceMutations';
import {
  createRecurringInvoice,
  updateRecurringInvoice,
  markGenerated as markGeneratedRpc,
  setActive as setActiveRpc,
  setAutoSend as setAutoSendRpc,
} from '../lib/recurringInvoiceMutations';
import type { RecurringInvoice } from '../types';

interface UseRecurringResult {
  templates: RecurringInvoice[];
  createTemplate: (data: Omit<RecurringInvoice, 'id' | 'last_generated_at'> & { business_id: string; created_by: string }) => Promise<boolean>;
  updateTemplate: (id: string, data: Partial<RecurringInvoice>) => Promise<boolean>;
  generateInvoice: (templateId: string) => Promise<boolean>;
  toggleActive: (id: string) => Promise<void>;
  toggleAutoSend: (id: string) => Promise<void>;
  loading: boolean;
  refresh: () => void;
}

function getNextDate(current: string, frequency: string): string {
  const d = new Date(current + 'T00:00:00');
  if (frequency === 'weekly') d.setDate(d.getDate() + 7);
  else if (frequency === 'biweekly') d.setDate(d.getDate() + 14);
  else if (frequency === 'monthly') d.setMonth(d.getMonth() + 1);
  else if (frequency === 'quarterly') d.setMonth(d.getMonth() + 3);
  else if (frequency === 'annually') d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split('T')[0];
}

export function useRecurring(businessId: string | undefined): UseRecurringResult {
  const [templates, setTemplates] = useState<RecurringInvoice[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('recurring_invoices')
        .select('id, client_name, client_email, line_items, subtotal, tax_rate, tax_amount, total, frequency, next_invoice_date, last_generated_at, is_active, auto_send, notes')
        .eq('business_id', businessId)
        .order('next_invoice_date', { ascending: true });
      if (error) throw error;
      setTemplates((data ?? []) as RecurringInvoice[]);
    } catch {
      // keep empty
    } finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  const rtTables = useMemo(() => ['recurring_invoices'], []);
  useRealtimeSubscription(businessId, rtTables, fetchAll);

  const createTemplate = useCallback(async (data: Omit<RecurringInvoice, 'id' | 'last_generated_at'> & { business_id: string; created_by: string }): Promise<boolean> => {
    try {
      // create_recurring_invoice (M-A4-017) atomically writes recurring_invoices
      // + activity_log. Server computes subtotal/tax_amount/total from line_items
      // (cents) + tax_rate; the caller's pre-computed totals (data.subtotal etc.)
      // are intentionally ignored. Identity (created_by) resolves server-side;
      // data.created_by is also ignored.
      const result = await createRecurringInvoice({
        businessId: data.business_id,
        lineItems: data.line_items,
        frequency: data.frequency,
        nextInvoiceDate: data.next_invoice_date,
        clientId: data.client_id ?? null,
        clientName: data.client_name,
        clientEmail: data.client_email,
        taxRate: data.tax_rate,
        isActive: data.is_active,
        autoSend: data.auto_send,
        notes: data.notes,
      });
      if (!result.success) return false;
      await fetchAll();
      return true;
    } catch { return false; }
  }, [fetchAll]);

  const updateTemplate = useCallback(async (id: string, data: Partial<RecurringInvoice>): Promise<boolean> => {
    try {
      // update_recurring_invoice COALESCE-updates only fields explicitly set.
      // line_items / tax_rate changes trigger server-side recompute of totals.
      const result = await updateRecurringInvoice({
        recurringInvoiceId: id,
        lineItems: data.line_items,
        frequency: data.frequency,
        nextInvoiceDate: data.next_invoice_date,
        clientId: data.client_id,
        clientName: data.client_name,
        clientEmail: data.client_email,
        taxRate: data.tax_rate,
        isActive: data.is_active,
        autoSend: data.auto_send,
        notes: data.notes,
      });
      if (!result.success) return false;
      await fetchAll();
      return true;
    } catch { return false; }
  }, [fetchAll]);

  const generateInvoice = useCallback(async (templateId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template) return false;

      // Phase B.3.2: routed through canonical RPC. Invoice number, totals,
      // business snapshot, identity, and activity_log resolve server-side.
      // Idempotency key is scoped to (template, period) so re-runs of the
      // same period return the same invoice instead of duplicating. Auto-send
      // behavior (template.auto_send) no longer applies at generation — the
      // RPC creates a draft. The legacy code flipped status='sent' directly
      // without actually invoking the send-invoice-email edge function (a
      // stealth bug); operator can send via the dashboard, or a future
      // scheduled-send job can pick up auto_send drafts. (See DEFERRED
      // §MoneyHardening.B3-2-Residual for the broader send-flow refactor.)
      const dueDate = new Date(template.next_invoice_date + 'T00:00:00');
      dueDate.setDate(dueDate.getDate() + 30); // default 30 day terms

      const result = await createInvoice({
        businessId,
        clientId: null,
        lineItems: template.line_items,
        dueDate: dueDate.toISOString().split('T')[0],
        notes: template.notes,
        taxRate: template.tax_rate,
        idempotencyKey: `recurring-${templateId}-${template.next_invoice_date}`,
        clientName: template.client_name,
        clientEmail: template.client_email,
      });
      if (!result.success) return false;

      // Advance the template's period regardless of already_created — the
      // intent ("a period has been generated for this template") is satisfied
      // either way. Without this, the operator could re-trigger generate and
      // get the same already_created invoice each time.
      const nextDate = getNextDate(template.next_invoice_date, template.frequency);
      await markGeneratedRpc(templateId, nextDate);

      await fetchAll();
      return true;
    } catch { return false; }
  }, [businessId, templates, fetchAll]);

  const toggleActive = useCallback(async (id: string) => {
    const t = templates.find(x => x.id === id);
    if (!t) return;
    await setActiveRpc(id, !t.is_active);
    await fetchAll();
  }, [templates, fetchAll]);

  const toggleAutoSend = useCallback(async (id: string) => {
    const t = templates.find(x => x.id === id);
    if (!t) return;
    await setAutoSendRpc(id, !t.auto_send);
    await fetchAll();
  }, [templates, fetchAll]);

  return { templates, createTemplate, updateTemplate, generateInvoice, toggleActive, toggleAutoSend, loading, refresh: fetchAll };
}
